import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/adminApi';
// Database service import removed - database functionality disabled

const uid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const STORAGE_KEY_PREFIX = 'emailStudio:inboxCache:v1';
const MAX_CACHE_MESSAGES = 300;

// No dummy data - inbox starts empty and loads from IMAP
const seedInbox = [];

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const normalizeEmails = (input) => {
  const pool = Array.isArray(input)
    ? input.flatMap((item) => String(item || '').split(/[\n,;]+/))
    : String(input || '')
        .split(/[\n,;]+/)
        .map((item) => item.trim());
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const cleaned = pool
    .map((v) => {
      const m = v.match(/<([^>]+)>/);
      return m ? m[1].trim() : v.trim();
    })
    .map((v) => v.toLowerCase())
    .filter((v) => regex.test(v));
  return Array.from(new Set(cleaned));
};

const formatApiError = (payload, fallback) => {
  if (!payload) return fallback;
  let message = payload.error ?? fallback;
  if (typeof message === 'object') {
    try {
      message = JSON.stringify(message);
    } catch {
      message = fallback;
    }
  }
  if (payload.details) {
    let details = payload.details;
    if (typeof details === 'object') {
      try {
        details = JSON.stringify(details);
      } catch {
        details = String(details);
      }
    }
    message = `${message} | ${details}`;
  }
  return message;
};

const applyMergeTokens = (text, row) => {
  if (!text || !row) return text || '';
  let result = String(text);
  const variants = new Map();
  const pick = (...keys) => {
    for (const key of keys) {
      const value = row?.[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return String(value);
      }
    }
    return '';
  };
  const register = (key, value) => {
    const raw = String(key || '').trim();
    if (!raw) return;
    const normalized = raw.toLowerCase();
    const compact = normalized.replace(/[^a-z0-9]+/g, '');
    const underscored = normalized.replace(/[^a-z0-9]+/g, '_');
    [raw, normalized, compact, underscored].forEach((candidate) => {
      if (candidate && !variants.has(candidate)) {
        variants.set(candidate, value);
      }
    });
  };

  Object.entries(row).forEach(([key, value]) => {
    register(key, value);
  });
  const primaryName = pick('Primary Contact', 'primary_contact', 'name', 'Name');
  const companyName = pick('DBA', 'dba', 'Legal Name', 'legal_name', 'company', 'Company');
  const firstName = primaryName ? primaryName.split(/\s+/)[0] : '';
  register('name', primaryName || companyName);
  register('full_name', primaryName || companyName);
  register('first_name', firstName);
  register('company', companyName);
  register('company_name', companyName);
  register('dot_number', pick('DOT', 'dot'));
  register('mc_number', pick('MC', 'mc'));
  register('phone_number', pick('Phone', 'phone', 'Cell Phone', 'cell_phone'));
  register('email_address', pick('Email', 'email'));
  register('state', pick('State', 'state', 'State(Physical)', 'state_physical', 'State(Mailing)', 'state_mailing'));
  variants.forEach((value, key) => {
    const token = new RegExp(`{{\\s*${String(key).replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}\\s*}}`, 'gi');
    result = result.replace(token, String(value ?? ''));
  });
  return result;
};

const mergeRowWithProfile = (row, profileSettings) => {
  const base = row ? { ...row } : {};
  const settings = profileSettings || {};
  const registerIfEmpty = (key, value) => {
    if (value === undefined || value === null || String(value).trim() === '') return;
    if (base[key] === undefined || base[key] === null || String(base[key]).trim() === '') {
      base[key] = value;
    }
  };

  registerIfEmpty('sender_name', settings.footerSenderName);
  registerIfEmpty('full_name', settings.footerSenderName);
  registerIfEmpty('company_name', settings.footerCompanyName);
  registerIfEmpty('company', settings.footerCompanyName);
  registerIfEmpty('address', settings.footerAddress);
  registerIfEmpty('business_address', settings.footerAddress);
  registerIfEmpty('phone', settings.footerPhone);
  registerIfEmpty('phone_number', settings.footerPhone);
  registerIfEmpty('email', settings.footerEmail);
  registerIfEmpty('email_address', settings.footerEmail);

  return base;
};

export const useEmailManager = () => {
  const { isAuthenticated, token } = useAuth();
  const envApiBase = process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_BASE_URL || '';
  const apiBase = useMemo(() => {
    if (envApiBase) return envApiBase.replace(/\/$/, '');
    if (typeof window === 'undefined') return '';
    return window.location.origin || '';
  }, [envApiBase]);
  const apiUrl = useCallback((path) => (apiBase ? `${apiBase}${path}` : path), [apiBase]);
  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}`, 'X-Auth-Token': token } : {}),
    [token]
  );
  const inboxCacheKey = useMemo(() => {
    if (typeof window === 'undefined') return `${STORAGE_KEY_PREFIX}:anon`;
    const safeToken = token ? String(token).replace(/[^a-z0-9]/gi, '').slice(0, 24) : 'anon';
    return `${STORAGE_KEY_PREFIX}:${safeToken || 'anon'}`;
  }, [token]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [inbox, setInbox] = useState(seedInbox);
  const [sent, setSent] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(null);
  const [statusText, setStatusText] = useState('');
  const [replyDrafts, setReplyDrafts] = useState({});
  const [filterUnread, setFilterUnread] = useState(false);
  const [fetchingInbox, setFetchingInbox] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [prefsError, setPrefsError] = useState('');
  const [profileSettings, setProfileSettings] = useState({});
  const accountsRef = useRef([]);
  const savedStatusMapRef = useRef({}); // Cache for saved message statuses
  const cacheHydratedRef = useRef(false);
  const cacheReadyRef = useRef(false);

  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

  const loadInboxCache = useCallback(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(inboxCacheKey);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (error) {
      console.warn('Failed to load inbox cache:', error);
      return null;
    }
  }, [inboxCacheKey]);

  const saveInboxCache = useCallback(
    (messages) => {
      if (typeof window === 'undefined') return;
      try {
        const compactMessages = (messages || [])
          .slice(0, MAX_CACHE_MESSAGES)
          .map((msg) => ({
            id: msg.id,
            originalId: msg.originalId || msg.id,
            accountId: msg.accountId || 'default',
            from: msg.from || '',
            to: msg.to || '',
            subject: msg.subject || '',
            preview: msg.preview || '',
            body: msg.body ? String(msg.body).slice(0, 4000) : '',
            timestamp: msg.timestamp || null,
            date: msg.date || null,
            status: msg.status || 'unread',
            read: msg.status ? msg.status !== 'unread' : !!msg.read,
            labels: Array.isArray(msg.labels) ? msg.labels : [],
          }));
        const payload = {
          version: 1,
          timestamp: Date.now(),
          messages: compactMessages,
          statusMap: savedStatusMapRef.current,
        };
        window.localStorage.setItem(inboxCacheKey, JSON.stringify(payload));
      } catch (error) {
        console.warn('Failed to save inbox cache:', error);
      }
    },
    [inboxCacheKey]
  );

  useEffect(() => {
    if (cacheHydratedRef.current) return;
    cacheHydratedRef.current = true;
    const cached = loadInboxCache();
    if (cached?.statusMap) {
      savedStatusMapRef.current = { ...cached.statusMap, ...savedStatusMapRef.current };
    }
    if (cached?.messages?.length) {
      const normalized = cached.messages.map((msg) => {
        const accountId = String(msg.accountId || 'default');
        const originalId = String(msg.originalId || msg.id);
        const compositeId = `${accountId}:${originalId}`;
        const status = msg.status || (msg.read ? 'read' : 'unread');
        savedStatusMapRef.current[compositeId] = status;
        savedStatusMapRef.current[originalId] = status;
        return {
          ...msg,
          id: compositeId,
          originalId,
          accountId,
          status,
        };
      });
      setInbox((prev) => {
        const isSeed =
          !prev ||
          prev.length === 0;
        if (isSeed) {
          return normalized;
        }
        const existingIds = new Set((prev || []).map((msg) => msg.id));
        const merged = [...normalized];
        (prev || []).forEach((msg) => {
          if (!existingIds.has(msg.id)) {
            merged.push(msg);
          }
        });
        return merged;
      });
    }
    cacheReadyRef.current = true;
  }, [loadInboxCache]);

  useEffect(() => {
    if (!cacheReadyRef.current) return;
    saveInboxCache(inbox);
  }, [inbox, saveInboxCache]);

  useEffect(() => {
    if (!selectedAccountId && accounts[0]) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [accounts, selectedAccountId]);

  const addAccount = useCallback((payload) => {
    const record = {
      id: payload.id || uid(), // Use existing id if provided, otherwise generate new one
      createdAt: payload.createdAt || new Date().toISOString(),
      status: payload.status || 'connected',
      ...payload,
    };
    setAccounts((prev) => {
      const next = [record, ...prev];
      accountsRef.current = next;
      if (isAuthenticated) {
        adminApi.saveAccounts(next, token).catch((e) => {
          const msg = e?.message || 'Failed to save accounts';
          setPrefsError(msg);
        });
      }
      return next;
    });
    setSelectedAccountId(record.id);
    return record;
  }, [isAuthenticated, token]);

  const updateAccount = useCallback((id, updates) => {
    setAccounts((prev) => {
      const next = prev.map((acc) =>
        acc.id === id ? { ...acc, ...updates, updatedAt: new Date().toISOString() } : acc
      );
      accountsRef.current = next;
      if (isAuthenticated) {
        adminApi.saveAccounts(next, token).catch((e) => {
          const msg = e?.message || 'Failed to save accounts';
          setPrefsError(msg);
        });
      }
      return next;
    });
  }, [isAuthenticated, token]);

  const deleteAccount = useCallback(
    (id) => {
      setAccounts((prev) => {
        const next = prev.filter((acc) => acc.id !== id);
        accountsRef.current = next;
        if (selectedAccountId === id) {
          setSelectedAccountId(next[0]?.id || '');
        }
        if (isAuthenticated) {
          adminApi.saveAccounts(next, token).catch((e) => {
            const msg = e?.message || 'Failed to save accounts';
            setPrefsError(msg);
          });
        }
        return next;
      });
    },
    [selectedAccountId, isAuthenticated, token]
  );

  // Server persistence of accounts/messages when authenticated
  const loadPrefsFromServer = useCallback(async () => {
    if (!isAuthenticated) {
      setPrefsLoaded(true);
      return;
    }
    try {
      const [prefsRes, accountsRes] = await Promise.all([
        adminApi.loadPrefs(token).catch(() => ({})),
        adminApi.loadAccounts(token).catch(() => ({})),
        // Database message loading removed - database functionality disabled
      ]);
      setPrefsError('');
      if (prefsRes?.prefs) {
        if (prefsRes.prefs.selectedAccountId) setSelectedAccountId(prefsRes.prefs.selectedAccountId);
        if (prefsRes.prefs.profileSettings && typeof prefsRes.prefs.profileSettings === 'object') {
          setProfileSettings(prefsRes.prefs.profileSettings);
        }
      }
      if (
        Array.isArray(accountsRes?.accounts) &&
        (accountsRes.accounts.length > 0 || accountsRef.current.length === 0)
      ) {
        setAccounts(accountsRes.accounts);
      }
    } catch (e) {
      const msg = e?.message || 'Failed to load preferences';
      setPrefsError(msg);
      console.warn('Prefs load failed', e);
    } finally {
      setPrefsLoaded(true);
    }
  }, [isAuthenticated, token]);

  const syncPrefsToServer = useCallback(async () => {
    if (!isAuthenticated || !prefsLoaded) return;
    try {
      await adminApi.savePrefs({ selectedAccountId }, token);
      setPrefsError('');
    } catch (e) {
      const msg = e?.message || 'Failed to save preferences';
      setPrefsError(msg);
      console.warn('Prefs save failed', e);
    }
  }, [isAuthenticated, token, prefsLoaded, accounts, inbox, sent, selectedAccountId]);

  useEffect(() => {
    if (isAuthenticated) {
      setPrefsLoaded(false);
      loadPrefsFromServer();
    } else {
      setPrefsLoaded(true);
    }
  }, [isAuthenticated, loadPrefsFromServer]);

  useEffect(() => {
    syncPrefsToServer();
  }, [accounts, inbox, sent, selectedAccountId, syncPrefsToServer]);

  const resolveEmailEndpoint = () => {
    if (apiBase) {
      return `${apiBase.replace(/\/$/, '')}/api/email_send.php`;
    }
    if (typeof window === 'undefined') return '/api/email_send.php';
    const { origin, port } = window.location;
    const isLocal = /https?:\/\/localhost(?::\d+)?/i.test(origin);
    if (isLocal && port === '3000') {
      return 'http://localhost:8080/api/email_send.php';
    }
    return '/api/email_send.php';
  };

  const testAccount = useCallback(
    async (accountLike) => {
      const acc =
        typeof accountLike === 'string'
          ? accounts.find((a) => a.id === accountLike)
          : accountLike;
      if (!acc) {
        throw new Error('Select or save an account first');
      }
      const endpoint = resolveEmailEndpoint();
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'ping',
          account: {
            host: acc.host,
            port: acc.port,
            secure: acc.secure,
            username: acc.username || acc.fromEmail,
            password: acc.password,
            fromEmail: acc.fromEmail || acc.username,
            fromName: acc.fromName || acc.label,
          },
        }),
      });
      const payload = await res.json();
      if (!res.ok || !payload?.ok) {
        const msg = formatApiError(payload, `SMTP test failed (${res.status})`);
        throw new Error(msg);
      }
      return {
        ok: true,
        message: payload?.message || `Connected to ${acc.provider || 'SMTP'} as ${acc.fromEmail || acc.username}`,
      };
    },
    [accounts]
  );

  const sendEmail = useCallback(
    async ({
      subject,
      body,
      bodyText,
      bodyHtml,
      recipients,
      accountId,
      replyToId,
      attachments = [],
      mergeData = null,
      campaignId = '',
      trackOpens = true,
      trackingProvider = 'none',
      trackingIntegration = null,
    }) => {
      const normalizedRecipients = normalizeEmails(recipients || []);
      if (!normalizedRecipients.length) {
        throw new Error('Add at least one recipient');
      }
      const account =
        accounts.find((acc) => acc.id === (accountId || selectedAccountId)) ||
        accounts[0];
      if (!account) {
        throw new Error('Pick an email account first');
      }

      const endpoint = resolveEmailEndpoint();
      const sendSingle = async (recipient) => {
        const rawRow = mergeData && mergeData[recipient] ? mergeData[recipient] : null;
        const row = mergeRowWithProfile(rawRow, profileSettings);
        const subj = row ? applyMergeTokens(subject || '', row) : (subject || '');
        const html = row ? applyMergeTokens(bodyHtml || '', row) : (bodyHtml || '');
        const text = row
          ? applyMergeTokens(bodyText || body || '', row)
          : (bodyText || body || '');
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          credentials: 'include',
          body: JSON.stringify({
            account: {
              accountId: account.id,
              host: account.host,
              port: account.port,
              secure: account.secure,
              username: account.username || account.fromEmail,
              password: account.password,
              fromEmail: account.fromEmail || account.username,
              fromName: account.fromName || account.label || account.fromEmail || account.username,
            },
            recipients: [recipient],
            subject: subj,
            body: text,
            bodyText: text,
            bodyHtml: html,
            attachments,
            replyToId,
            campaignId,
            trackOpens,
            trackingProvider,
            trackingIntegration,
          }),
        });
        const raw = await res.text();
        let payload = null;
        if (raw) {
          try {
            payload = JSON.parse(raw);
          } catch (err) {
            payload = null;
          }
        }
        if (!payload && res.ok) {
          payload = { ok: true };
        }
        if (!res.ok || !payload?.ok) {
          const fallback = raw || `SMTP send failed (${res.status})`;
          const msg = formatApiError(payload, fallback);
          throw new Error(msg);
        }
        const mid = payload?.messageId || uid();
        const record = {
          id: mid,
          messageId: mid,
          trackingId: payload?.trackingId || payload?.messageId || mid,
          trackingProvider: payload?.trackingProvider || trackingProvider,
          trackingError: payload?.trackingError || '',
          accountId: account.id,
          subject: subj,
          body: html || text,
          recipients: [recipient],
          status: 'sent',
          parentId: replyToId || null,
          sentAt: new Date().toISOString(),
        };
        setSent((prev) => [record, ...prev].slice(0, 200));
        return record;
      };

      setSending(true);
      setStatusText('');
      const showProgress = normalizedRecipients.length > 1;
      if (showProgress) {
        setSendProgress({
          total: normalizedRecipients.length,
          sent: 0,
          failed: 0,
          current: 0,
        });
      } else {
        setSendProgress(null);
      }

      let sentCount = 0;
      let failedCount = 0;
      const failedRecipients = [];
      try {
        const records = [];
        for (let i = 0; i < normalizedRecipients.length; i += 1) {
          const recipient = normalizedRecipients[i];
          try {
            const record = await sendSingle(recipient);
            records.push(record);
            sentCount += 1;
          } catch (err) {
            failedCount += 1;
            failedRecipients.push(recipient);
          }
          if (showProgress) {
            setSendProgress((prev) =>
              prev
                ? {
                    ...prev,
                    sent: sentCount,
                    failed: failedCount,
                    current: i + 1,
                  }
                : prev
            );
          }
        }

        setStatusText(
          failedCount
            ? `Sent ${sentCount}, failed ${failedCount} (${failedRecipients.slice(0, 3).join(', ')}${failedRecipients.length > 3 ? '…' : ''}).`
            : `Sent to ${sentCount} recipients.`
        );

        if (sentCount === 0) {
          throw new Error('Failed to send any emails.');
        }

        return records.length === 1 ? records[0] : { ok: true, sentCount, failedCount };
      } finally {
        setSending(false);
      }
    },
    [accounts, selectedAccountId, authHeaders]
  );

  const fetchInbox = useCallback(
    async (accountIdOverride) => {
      const account =
        accounts.find((acc) => acc.id === (accountIdOverride || selectedAccountId)) ||
        accounts[0];
      if (!account) throw new Error('Pick an email account first');
      if (!account.imapHost && !account.popHost) throw new Error('IMAP/POP host not set for this account');

      const imapHostRaw = String(account.imapHost || '').trim();
      const imapPortRaw = Number(account.imapPort || 993);
      const popHostRaw = String(account.popHost || '').trim();
      const popPortRaw = Number(account.popPort || 995);
      const looksLikePop =
        (imapHostRaw && imapHostRaw.toLowerCase().includes('pop.')) ||
        (!popHostRaw && imapPortRaw === 995);
      const resolved = looksLikePop && !popHostRaw
        ? {
            imapHost: '',
            imapPort: 993,
            imapSecure: account.imapSecure !== false,
            popHost: imapHostRaw,
            popPort: imapPortRaw || 995,
            popSecure: account.imapSecure !== false,
          }
        : {
            imapHost: imapHostRaw,
            imapPort: imapPortRaw,
            imapSecure: account.imapSecure !== false,
            popHost: popHostRaw,
            popPort: popPortRaw,
            popSecure: account.popSecure !== false,
          };
      setFetchingInbox(true);
      try {
        const endpoint = resolveEmailEndpoint().replace('email_send.php', 'email_fetch.php');
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            account: {
              imapHost: resolved.imapHost,
              imapPort: resolved.imapPort,
              imapSecure: resolved.imapSecure,
              popHost: resolved.popHost,
              popPort: resolved.popPort,
              popSecure: resolved.popSecure,
              username: account.username || account.fromEmail,
              password: account.password,
              mailbox: account.mailbox || 'INBOX',
            },
          }),
        });
        const raw = await res.text();
        let payload = null;
        try {
          payload = raw ? JSON.parse(raw) : null;
        } catch (err) {
          throw new Error('Invalid response from inbox server.');
        }
        if (!res.ok || !payload?.ok) {
          const msg = formatApiError(payload, `IMAP fetch failed (${res.status})`);
          throw new Error(msg);
        }
        const msgs = Array.isArray(payload.messages) ? payload.messages : [];
        const accountKey = String(account.id || accountIdOverride || 'default');
        setInbox((prev) => {
          const normalizedPrev = (prev || []).map((msg) => {
            if (typeof msg.id === 'string' && msg.id.includes(':')) {
              return msg;
            }
            const originalId = String(msg.originalId || msg.id);
            const prevAccount = String(msg.accountId || accountKey);
            const compositeId = `${prevAccount}:${originalId}`;
            const cachedStatus =
              savedStatusMapRef.current[compositeId] ?? savedStatusMapRef.current[originalId];
            if (cachedStatus && !savedStatusMapRef.current[compositeId]) {
              savedStatusMapRef.current[compositeId] = cachedStatus;
            }
            const status = cachedStatus || msg.status || (msg.read ? 'read' : 'unread');
            savedStatusMapRef.current[compositeId] = status;
            savedStatusMapRef.current[originalId] = status;
            return {
              ...msg,
              id: compositeId,
              originalId,
              accountId: prevAccount,
              status,
            };
          });
          const otherAccounts = normalizedPrev.filter((msg) => String(msg.accountId || '') !== accountKey);
          const freshAccountMessages = msgs.map((m) => {
            const originalId = String(m.id);
            const compositeId = `${accountKey}:${originalId}`;
            const savedStatus =
              savedStatusMapRef.current[compositeId] ?? savedStatusMapRef.current[originalId];
            const status = savedStatus || m.status || (m.read ? 'read' : 'unread');
            savedStatusMapRef.current[compositeId] = status;
            savedStatusMapRef.current[originalId] = status;
            return {
              ...m,
              id: compositeId,
              originalId,
              accountId: accountKey,
              status,
            };
          });
          return [...freshAccountMessages, ...otherAccounts].sort(
            (a, b) => new Date(b.timestamp || b.date || 0) - new Date(a.timestamp || a.date || 0)
          );
        });
        return msgs;
      } finally {
        setFetchingInbox(false);
      }
    },
    [accounts, selectedAccountId]
  );

  const appendInbox = useCallback(
    (message) => {
      const accountId = String(message.accountId || selectedAccountId || 'default');
      const originalId = String(message.originalId || message.id || uid());
      const compositeId = `${accountId}:${originalId}`;
      const status = message.status || (message.read ? 'read' : 'unread') || 'unread';
      savedStatusMapRef.current[compositeId] = status;
      savedStatusMapRef.current[originalId] = status;
      setInbox((prev) => {
        const withoutExisting = (prev || []).filter((msg) => msg.id !== compositeId);
        return [
          {
            ...message,
            id: compositeId,
            originalId,
            accountId,
            status,
            timestamp: message.timestamp || new Date().toISOString(),
          },
          ...withoutExisting,
        ];
      });
    },
    [selectedAccountId]
  );

  /**
   * Fetch inbox for ALL accounts in parallel — true unified inbox.
   * Loops through every account with a valid IMAP/POP host and
   * merges results. Errors for individual accounts are silently ignored
   * so that one broken account doesn't block others.
   */
  const fetchAllInboxes = useCallback(async () => {
    if (!accounts || accounts.length === 0) {
      throw new Error('No email accounts configured');
    }
    const validAccounts = accounts.filter(
      (acc) => acc.imapHost || acc.popHost
    );
    if (validAccounts.length === 0) {
      throw new Error('No accounts have IMAP/POP configured');
    }
    setFetchingInbox(true);
    try {
      const results = await Promise.allSettled(
        validAccounts.map((acc) => fetchInbox(acc.id))
      );
      const allMessages = [];
      results.forEach((r, i) => {
        if (r.status === 'fulfilled' && Array.isArray(r.value)) {
          allMessages.push(...r.value);
        }
      });
      return allMessages;
    } finally {
      setFetchingInbox(false);
    }
  }, [accounts, fetchInbox]);

  const fetchTrackingStats = useCallback(async (messageId) => {
    if (!messageId) throw new Error('messageId required');
    const endpoint = resolveEmailEndpoint().replace('email_send.php', 'track_stats.php');
    const url = `${endpoint}?id=${encodeURIComponent(messageId)}`;
    const res = await fetch(url, { headers: authHeaders });
    const payload = await res.json();
    if (!res.ok || !payload?.ok) {
      throw new Error(payload?.error || `Tracking fetch failed (${res.status})`);
    }
    return payload;
  }, [authHeaders]);

  const fetchTrackingTotals = useCallback(async () => {
    const endpoint = resolveEmailEndpoint().replace('email_send.php', 'track_stats.php');
    const res = await fetch(endpoint, { headers: authHeaders });
    const payload = await res.json();
    if (!res.ok || !payload?.ok) {
      throw new Error(payload?.error || `Tracking fetch failed (${res.status})`);
    }
    return payload;
  }, [authHeaders]);

  const fetchTrackingList = useCallback(async (limit = 200) => {
    const endpoint = resolveEmailEndpoint().replace('email_send.php', 'track_stats.php');
    const url = `${endpoint}?action=list&limit=${encodeURIComponent(limit)}`;
    const res = await fetch(url, { headers: authHeaders });
    const payload = await res.json();
    if (!res.ok || !payload?.ok) {
      throw new Error(payload?.error || `Tracking list failed (${res.status})`);
    }
    return payload;
  }, [authHeaders]);

  // Fetch saved message read statuses from database
  const fetchMessageStatuses = useCallback(async () => {
    try {
      const endpoint = resolveEmailEndpoint().replace('email_send.php', 'message_status.php');
      const res = await fetch(endpoint, { headers: authHeaders });
      const payload = await res.json();
      if (res.ok && payload?.ok && payload.statusMap) {
        // Update the cache
        savedStatusMapRef.current = { ...savedStatusMapRef.current, ...payload.statusMap };
        return payload.statusMap;
      }
    } catch (e) {
      console.warn('Failed to fetch message statuses:', e);
    }
    return {};
  }, [authHeaders]);

  // Apply saved statuses to inbox
  const applyMessageStatuses = useCallback((statusMap) => {
    if (!statusMap || Object.keys(statusMap).length === 0) return;
    const normalizedEntries = {};
    Object.entries(statusMap).forEach(([id, status]) => {
      if (!id) return;
      normalizedEntries[id] = status;
      if (typeof id === 'string' && id.includes(':')) {
        const baseId = id.split(':').slice(1).join(':');
        if (baseId) {
          normalizedEntries[baseId] = status;
        }
      }
    });
    savedStatusMapRef.current = { ...savedStatusMapRef.current, ...normalizedEntries };
    setInbox((prev) =>
      prev.map((msg) => {
        const compositeId = msg.id;
        const baseId =
          typeof compositeId === 'string' && compositeId.includes(':')
            ? compositeId.split(':').slice(1).join(':')
            : compositeId;
        const savedStatus = normalizedEntries[compositeId] ?? normalizedEntries[baseId];
        if (savedStatus) {
          savedStatusMapRef.current[compositeId] = savedStatus;
          if (baseId) savedStatusMapRef.current[baseId] = savedStatus;
          return { ...msg, status: savedStatus };
        }
        return msg;
      })
    );
  }, []);

  // Save message status to database
  const saveMessageStatus = useCallback(async (messageId, status) => {
    // Update cache immediately
    savedStatusMapRef.current[messageId] = status;
    if (typeof messageId === 'string' && messageId.includes(':')) {
      const baseId = messageId.split(':').slice(1).join(':');
      if (baseId) {
        savedStatusMapRef.current[baseId] = status;
      }
    }
    try {
      const endpoint = resolveEmailEndpoint().replace('email_send.php', 'message_status.php');
      await fetch(endpoint, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageId, status }),
      });
    } catch (e) {
      console.warn('Failed to save message status:', e);
    }
  }, [authHeaders]);

  const setMessageStatus = useCallback((id, status) => {
    // Update local state immediately
    setInbox((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, status } : msg))
    );
    // Persist to database (fire and forget)
    saveMessageStatus(id, status);
  }, [saveMessageStatus]);

  const replyToMessage = useCallback(
    async (messageId, replyBody, accountId) => {
      const message = inbox.find((m) => m.id === messageId);
      if (!message) throw new Error('Message not found');
      return sendEmail({
        subject: `Re: ${message.subject}`,
        body: replyBody,
        recipients: [message.from],
        accountId: accountId || message.accountId || selectedAccountId,
      });
    },
    [inbox, sendEmail, selectedAccountId]
  );

  const deleteMessage = useCallback((id) => {
    setInbox((prev) => prev.filter((m) => m.id !== id));
    // Also drop any sent replies tied to this message so the conversation disappears locally
    setSent((prev) => prev.filter((s) => s.parentId !== id && s.id !== id));
  }, []);

  const stats = useMemo(() => {
    const unread = inbox.filter((m) => m.status === 'unread').length;
    return { unread, totalInbox: inbox.length, totalSent: sent.length };
  }, [inbox, sent]);

  return {
    accounts,
    selectedAccountId,
    setSelectedAccountId,
    inbox,
    sent,
    sending,
    statusText,
    addAccount,
    updateAccount,
    deleteAccount,
    testAccount,
    sendEmail,
    fetchInbox,
    fetchAllInboxes,
    appendInbox,
    fetchTrackingStats,
    fetchTrackingTotals,
    fetchTrackingList,
    setMessageStatus,
    fetchMessageStatuses,
    applyMessageStatuses,
    replyToMessage,
    deleteMessage,
    stats,
    prefsError,
    filterUnread,
    setFilterUnread,
    fetchingInbox,
    replyDrafts,
    setReplyDrafts,
    sendProgress,
  };
};
